import type { ScenarioRow } from "./scenarios";

export type LoanStatus =
  | "Draft"
  | "Active"
  | "On Track"
  | "Ahead of Schedule"
  | "Behind Schedule"
  | "Needs Review"
  | "Paid Off"
  | "Archived";

export const LOAN_STATUSES: LoanStatus[] = [
  "Draft",
  "Active",
  "On Track",
  "Ahead of Schedule",
  "Behind Schedule",
  "Needs Review",
  "Paid Off",
  "Archived",
];

export type TimelineCategory =
  | "loan_created"
  | "payment_confirmed"
  | "payment_issue"
  | "lump_sum"
  | "stewardship"
  | "report"
  | "archived";

export const TIMELINE_CATEGORY_LABEL: Record<TimelineCategory, string> = {
  loan_created: "Loan commitments",
  payment_confirmed: "Payments",
  payment_issue: "Payment issues",
  lump_sum: "Lump sums",
  stewardship: "Stewardship",
  report: "Reports",
  archived: "Archived",
};

export type ActualStatus =
  | "paid_as_planned"
  | "paid_different"
  | "skipped"
  | "paid_late"
  | "partial";

export type PaymentActualRow = {
  scenario_id: string;
  payment_number: number;
  planned_date: string | null;
  planned_amount: number | null;
  status: ActualStatus;
  actual_amount: number | null;
  actual_date: string | null;
  note: string | null;
};

export type TimelineEvent = {
  id: string;
  date: string; // ISO
  category: TimelineCategory;
  title: string;
  description?: string;
  amount?: number;
  loanId?: string | null;
  loanName?: string | null;
  purpose?: string | null;
  scenarioId: string;
  status?: LoanStatus;
};

function safeGet<T>(obj: unknown, key: string): T | undefined {
  if (obj && typeof obj === "object" && key in obj) {
    return (obj as Record<string, T>)[key];
  }
  return undefined;
}

function purposeLabel(scenario: ScenarioRow): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = scenario.data ?? {};
  const p = data.purpose;
  if (!p) return null;
  if (typeof p === "string") return p;
  return (
    p.customLabel ||
    p.label ||
    p.category ||
    p.type ||
    null
  );
}

function loanAmount(scenario: ScenarioRow): number {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = scenario.data ?? {};
  return Number(data?.setup?.loanAmount) || 0;
}

function stewardshipScore(scenario: ScenarioRow): number | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = scenario.data ?? {};
  const s = data?.stewardship;
  if (!s) return null;
  const score = Number(s.score ?? s.totalScore);
  return Number.isFinite(score) && score > 0 ? score : null;
}

function stewardshipBand(score: number): string {
  if (score >= 80) return "Strong Steward";
  if (score >= 60) return "Steady Steward";
  if (score >= 40) return "Building Discipline";
  return "Needs Focus";
}

export function computeLoanStatus(
  scenario: ScenarioRow,
  actuals: PaymentActualRow[]
): LoanStatus {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = scenario.data ?? {};
  if ((scenario as unknown as { archived_at?: string | null }).archived_at) {
    return "Archived";
  }
  if (scenario.is_draft || !scenario.loan_id) return "Draft";

  const mine = actuals.filter((a) => a.scenario_id === scenario.id);
  if (mine.length === 0) return "Active";

  const today = new Date().toISOString().slice(0, 10);
  const dueSoFar = mine.filter((a) => (a.planned_date ?? "") <= today);

  const skipped = mine.filter((a) => a.status === "skipped").length;
  const partial = mine.filter((a) => a.status === "partial").length;
  const late = mine.filter((a) => a.status === "paid_late").length;
  const issueCount = skipped + partial + late;

  if (issueCount >= 3 || (dueSoFar.length > 0 && skipped / dueSoFar.length >= 0.2)) {
    return "Needs Review";
  }

  const totalPlanned = mine.reduce((s, a) => s + (Number(a.planned_amount) || 0), 0);
  const totalActual = mine.reduce(
    (s, a) => s + (a.status === "skipped" ? 0 : Number(a.actual_amount) || 0),
    0
  );

  if (issueCount > 0) return "Behind Schedule";
  if (totalPlanned > 0 && totalActual > totalPlanned * 1.05) return "Ahead of Schedule";

  // Paid off: all planned numbers tracked and last planned date in past
  const setup = data?.setup;
  const numPayments = Number(setup?.numberOfPayments) || 0;
  if (numPayments > 0 && mine.length >= numPayments) {
    const lastPlanned = [...mine].sort((a, b) =>
      (a.planned_date ?? "").localeCompare(b.planned_date ?? "")
    ).slice(-1)[0];
    if (lastPlanned?.planned_date && lastPlanned.planned_date <= today) {
      return "Paid Off";
    }
  }

  return "On Track";
}

export function buildTimelineEvents(
  scenarios: ScenarioRow[],
  actuals: PaymentActualRow[]
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  for (const s of scenarios) {
    const status = computeLoanStatus(s, actuals);
    const base = {
      scenarioId: s.id,
      loanId: s.loan_id,
      loanName: s.name,
      purpose: purposeLabel(s),
      status,
    };

    // 1. Loan created
    events.push({
      id: `${s.id}-created`,
      date: s.created_at,
      category: "loan_created",
      title: "Loan Commitment Created",
      amount: loanAmount(s),
      ...base,
    });

    // 2. Stewardship
    const score = stewardshipScore(s);
    if (score != null) {
      events.push({
        id: `${s.id}-stewardship`,
        date: s.updated_at,
        category: "stewardship",
        title: "Stewardship Assessment Completed",
        description: `${stewardshipBand(score)} — ${score}/100`,
        ...base,
      });
    }

    // 3. Report available (non-draft)
    if (!s.is_draft) {
      events.push({
        id: `${s.id}-report`,
        date: s.updated_at,
        category: "report",
        title: "Family Banking Commitment Agreement™ Available",
        description: "Generated from the current scenario.",
        ...base,
      });
    }

    // 4. Lump sums (planned)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ls = (s.data as any)?.lumpSumConfig?.scenarios;
    if (Array.isArray(ls)) {
      for (const entry of ls) {
        const amt = Number(entry?.amount) || 0;
        const date = entry?.date;
        if (!date || amt <= 0) continue;
        const src = entry?.source ? String(entry.source).replace(/_/g, " ") : "lump sum";
        events.push({
          id: `${s.id}-ls-${date}-${amt}`,
          date,
          category: "lump_sum",
          title: "Lump Sum Planned",
          description: `${formatMoney(amt)} ${src}`,
          amount: amt,
          ...base,
        });
      }
    }

    // 5. Archived
    const archivedAt = (s as unknown as { archived_at?: string | null }).archived_at;
    if (archivedAt) {
      events.push({
        id: `${s.id}-archived`,
        date: archivedAt,
        category: "archived",
        title: "Loan Archived",
        ...base,
      });
    }
  }

  // 6. Payment actuals
  const scenarioById = new Map(scenarios.map((s) => [s.id, s]));
  for (const a of actuals) {
    const s = scenarioById.get(a.scenario_id);
    if (!s) continue;
    const status = computeLoanStatus(s, actuals);
    const date = a.actual_date ?? a.planned_date ?? s.updated_at;
    const amt = a.actual_amount ?? a.planned_amount ?? 0;
    let title = "Payment Confirmed";
    let category: TimelineCategory = "payment_confirmed";
    let description = "";
    switch (a.status) {
      case "paid_as_planned":
        title = "Payment Confirmed";
        description = `${formatMoney(amt)} paid as planned`;
        break;
      case "paid_different":
        title = "Payment (Different Amount)";
        description = `${formatMoney(amt)} paid (planned ${formatMoney(a.planned_amount ?? 0)})`;
        break;
      case "paid_late":
        title = "Payment Paid Late";
        category = "payment_issue";
        description = `${formatMoney(amt)} paid late`;
        break;
      case "partial":
        title = "Partial Payment";
        category = "payment_issue";
        description = `${formatMoney(amt)} partial (planned ${formatMoney(a.planned_amount ?? 0)})`;
        break;
      case "skipped":
        title = "Payment Missed";
        category = "payment_issue";
        description = `Scheduled ${formatMoney(a.planned_amount ?? 0)} payment skipped`;
        break;
    }
    events.push({
      id: `${s.id}-pay-${a.payment_number}`,
      date,
      category,
      title,
      description: a.note ? `${description} — ${a.note}` : description,
      amount: amt,
      scenarioId: s.id,
      loanId: s.loan_id,
      loanName: s.name,
      purpose: purposeLabel(s),
      status,
    });
  }

  events.sort((a, b) => b.date.localeCompare(a.date));
  return events;
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export type TimelineSummary = {
  totalCommitments: number;
  activeLoans: number;
  paidOffLoans: number;
  totalAmountModeled: number;
  totalPaidToDate: number;
  avgStewardshipScore: number | null;
};

export function summarize(
  scenarios: ScenarioRow[],
  actuals: PaymentActualRow[]
): TimelineSummary {
  const statuses = scenarios.map((s) => computeLoanStatus(s, actuals));
  const nonArchived = scenarios.filter(
    (_, i) => statuses[i] !== "Archived"
  );

  const totalCommitments = scenarios.filter((s) => !s.is_draft).length;
  const activeLoans = statuses.filter(
    (st) => st === "Active" || st === "On Track" || st === "Ahead of Schedule" || st === "Behind Schedule" || st === "Needs Review"
  ).length;
  const paidOffLoans = statuses.filter((st) => st === "Paid Off").length;

  const totalAmountModeled = nonArchived.reduce((sum, s) => sum + loanAmount(s), 0);

  const totalPaidToDate = actuals.reduce(
    (sum, a) => sum + (a.status === "skipped" ? 0 : Number(a.actual_amount) || 0),
    0
  );

  const scores = scenarios
    .map((s) => stewardshipScore(s))
    .filter((x): x is number => x != null);
  const avgStewardshipScore =
    scores.length === 0 ? null : Math.round(scores.reduce((s, x) => s + x, 0) / scores.length);

  return {
    totalCommitments,
    activeLoans,
    paidOffLoans,
    totalAmountModeled,
    totalPaidToDate,
    avgStewardshipScore,
  };
}